/**
 * The handoffs queue — the screen that decides whether the human loop works.
 *
 * Two things are deliberate. First, the SLA clocks are live and coloured: a handoff nobody has
 * claimed for an hour should look wrong from across the room. Second, closing REQUIRES an
 * outcome; the modal has no way to close without one, mirroring the database CHECK that makes
 * an outcome-less close impossible. The 97%-never-actioned failure is a UI failure as much as
 * a schema one.
 *
 * Author: Danish Khan
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import clsx from 'clsx';
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { api } from '../lib/api.ts';
import { formatAgo, formatDuration } from '../lib/format.ts';
import { canWrite, roleGate } from '../lib/pii.ts';
import { useUser } from '../lib/session.tsx';
import type {
  Handoff, HandoffNotification, HandoffOutcome, NotificationStatus,
} from '../lib/types.ts';
import { Button } from '../components/Button.tsx';
import { DataTable } from '../components/DataTable.tsx';
import { Drawer } from '../components/Drawer.tsx';
import { ErrorState } from '../components/ErrorState.tsx';
import { Pill, TierPill } from '../components/Pill.tsx';
import { useToast } from '../components/Toast.tsx';

const OUTCOMES: { key: HandoffOutcome; label: string }[] = [
  { key: 'won', label: 'Won' },
  { key: 'lost', label: 'Lost' },
  { key: 'no_show', label: 'No show' },
  { key: 'not_reached', label: 'Not reached' },
  { key: 'duplicate', label: 'Duplicate' },
];

/** The default `limit` of `readHandoffs` in `api/routes/handoffs.ts`. Not on the wire. */
const HANDOFF_CAP = 200;

/**
 * Was anyone on the team ACTUALLY told?
 *
 * The `inbox` adapter is a no-op that reports `sent` unconditionally — its whole job is to assert
 * that this console has the handoff, which it always does. Counting it here would make every
 * handover look notified, including the ones where the WhatsApp DM was skipped for want of an
 * approved template. So it is excluded, deliberately: "notified" means a human was reached
 * somewhere other than this screen.
 */
export const reachedTeam = (h: Pick<Handoff, 'notifications'>): HandoffNotification[] =>
  h.notifications.filter(
    (n) => n.status === 'sent' && n.adapter !== 'inbox' && n.delivery !== 'failed',
  );

/** Why an adapter did not reach anyone, in words a salesperson can act on. */
const NOTIFY_REASON: Record<NotificationStatus, string> = {
  sent: 'notified',
  failed: 'send failed',
  skipped_no_template: 'no approved WhatsApp template',
  skipped_not_configured: 'channel not configured',
  skipped_no_target: 'no rep configured',
  skipped_group_disabled: 'group notifications off',
};

const ADAPTER_LABEL: Record<HandoffNotification['adapter'], string> = {
  inbox: 'This console',
  telegram: 'Telegram',
  whatsapp_dm: 'WhatsApp DM',
  whatsapp_group: 'WhatsApp group',
};

/**
 * A handoff's status in words. `in_progress` is a rep who handed the thread back to the agent and
 * still owns the outcome (`routes/handoffs.ts`), and the queue used to print it as the raw key.
 */
const STATUS_LABEL: Record<Handoff['status'], string> = {
  open: 'open', claimed: 'claimed', in_progress: 'handed back', closed: 'closed',
};

/**
 * What an escalation rung did, in words. The API sends `kind: step_N` and the ladder action as
 * `note`; the drawer printed both raw — `step_1 — repost` — which is the escalation ladder
 * describing itself to the person it exists to prompt.
 */
const ESCALATION_LABEL: Record<string, string> = {
  repost: 'Reminder re-sent — still unclaimed',
  overdue_post: 'Marked overdue — the customer is waiting',
  owner_dm_and_flag: 'Owner alerted and flagged',
  opened_degraded: 'Opened without reaching anyone on the team',
};
const escalationLabel = (note: string): string => ESCALATION_LABEL[note] ?? note.replace(/_/gu, ' ');

/** The human label for an outcome, falling back to the key rather than rendering blank. */
export const outcomeLabel = (o: HandoffOutcome): string =>
  OUTCOMES.find((x) => x.key === o)?.label ?? o;

const BASIS_TONE = { customer_consent: 'ok', policy_required: 'warn', system_inferred: 'neutral' } as const;

const column = createColumnHelper<Handoff>();

export function HandoffsPage(): ReactNode {
  const user = useUser();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { toast, toastError } = useToast();
  const queryClient = useQueryClient();
  const status = (params.get('status') ?? 'all') as 'open' | 'claimed' | 'closed' | 'all';
  // `ops` is deliberately below `rep`: HeyOne's own operators run the system, they do not work
  // the client's queue. The API refuses them; here they see why instead of a 403 toast.
  const mayWork = canWrite(user.role);
  const gate = roleGate(mayWork, 'Claiming, releasing and closing are for roles rep and above', user.role);

  const [detail, setDetail] = useState<Handoff | null>(null);
  const [closing, setClosing] = useState<Handoff | null>(null);

  const handoffs = useQuery({ queryKey: ['handoffs', status], queryFn: () => api.handoffs(status) });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['handoffs'] });
    void queryClient.invalidateQueries({ queryKey: ['inbox'] });
    void queryClient.invalidateQueries({ queryKey: ['metrics'] });
  };

  const claim = useMutation({
    mutationFn: (id: string) => api.claimHandoff(id),
    onSuccess: (h) => {
      toast(`Claimed ${h.code}`, { tone: 'ok', body: 'The customer is yours until you close it with an outcome.' });
      refresh();
    },
    onError: (err) => toastError('claim that handoff', err),
  });

  const release = useMutation({
    mutationFn: (id: string) => api.releaseHandoff(id),
    onSuccess: (h) => {
      toast(`Released ${h.code}`, { tone: 'ok', body: 'The agent answers that thread again. It is still open.' });
      refresh();
    },
    onError: (err) => toastError('release that handoff', err),
  });

  const close = useMutation({
    mutationFn: (vars: { id: string; outcome: HandoffOutcome; reason: string }) =>
      api.closeHandoff(vars.id, vars.outcome, vars.reason),
    onSuccess: (h) => {
      // The label, like the queue row beside it. The row was fixed on 22 Sep 2026 to say "Not
      // reached" and this toast went on saying `not_reached` — the same raw key, one line away.
      toast(`Closed ${h.code} — ${h.outcome ? outcomeLabel(h.outcome) : 'no outcome'}`, { tone: 'ok' });
      setClosing(null);
      setDetail(null);
      refresh();
    },
    onError: (err) => toastError('close that handoff', err),
  });

  const columns = useMemo(
    () => [
      column.accessor('displayName', {
        header: 'Customer',
        meta: { width: 'minmax(10rem, 1fr)' },
        cell: (ctx) => (
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-fg">{ctx.getValue()}</p>
            <p className="font-mono text-[11px] text-fg-subtle">{ctx.row.original.code}</p>
          </div>
        ),
      }),
      column.accessor('reasonLabel', {
        header: 'Why',
        meta: { width: 'minmax(12rem, 1.2fr)' },
        cell: (ctx) => (
          <div className="min-w-0">
            <p className="truncate text-xs text-fg">{ctx.getValue()}</p>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              <Pill tone={BASIS_TONE[ctx.row.original.basis]}>{ctx.row.original.basis.replace('_', ' ')}</Pill>
              {ctx.row.original.escalations.length > 0 && (
                <Pill tone="danger">
                  {ctx.row.original.escalations.length}{' '}
                  {ctx.row.original.escalations.length === 1 ? 'escalation' : 'escalations'}
                </Pill>
              )}
              {/*
                The signal that was missing entirely: an open handover nobody was told about looked
                exactly like one the sales desk already had. Only shown when it is bad news —
                a notified handover needs no badge.
              */}
              {reachedTeam(ctx.row.original).length === 0 && (
                <Pill tone="danger">Not notified</Pill>
              )}
            </div>
          </div>
        ),
      }),
      column.accessor('quote', {
        header: 'Their words',
        meta: { width: 'minmax(12rem, 1.6fr)' },
        cell: (ctx) => {
          const quote = ctx.getValue();
          return quote ? (
            <p className="truncate text-xs italic text-fg-muted" title={quote}>
              “{quote}”
            </p>
          ) : (
            <p className="text-[11px] text-fg-subtle">
              {ctx.row.original.basis === 'system_inferred' ? 'nobody said yes — we decided' : 'policy, not consent'}
            </p>
          );
        },
      }),
      column.accessor('score', {
        header: 'Tier',
        meta: { width: '96px' },
        cell: (ctx) => <TierPill tier={ctx.row.original.tier} score={ctx.getValue()} />,
      }),
      column.accessor('openedAt', {
        header: 'Waiting',
        meta: { width: '110px' },
        // Both clocks come from the SERVER (`ageMs`, `claimWaitMs`), not from `Date.now()` here.
        // The browser's clock can be minutes out, and an SLA column computed against a wrong
        // clock is worse than no SLA column.
        cell: (ctx) => {
          const h = ctx.row.original;
          const late = h.claimWaitMs === null && h.ageMs > 60 * 60_000;
          return (
            <div className="text-xs">
              <p className={clsx(late ? 'font-semibold text-danger' : 'text-fg')}>
                {formatDuration(h.claimWaitMs ?? h.ageMs)}
              </p>
              <p className="text-[10px] text-fg-subtle">{h.claimWaitMs === null ? 'unclaimed' : 'to claim'}</p>
            </div>
          );
        },
      }),
      column.accessor('status', {
        header: 'Status',
        meta: { width: '140px' },
        cell: (ctx) => {
          const h = ctx.row.original;
          return (
            <div className="flex flex-col items-start gap-0.5">
              <Pill tone={h.status === 'open' ? 'danger' : h.status === 'closed' ? 'neutral' : 'warn'} dot>
                {STATUS_LABEL[h.status] ?? h.status}
              </Pill>
              {h.claimedBy && <span className="text-[10px] text-fg-subtle">{h.claimedBy}</span>}
              {/* The label, not the key. `OUTCOMES` already carries both and the close form uses
                  them; the queue printed the raw `not_reached` / `no_show` beside them. */}
              {h.outcome && (
                <span className="text-[10px] font-medium text-fg-muted">→ {outcomeLabel(h.outcome)}</span>
              )}
            </div>
          );
        },
      }),
      column.display({
        id: 'actions',
        header: '',
        meta: { width: '180px' },
        cell: (ctx) => {
          const h = ctx.row.original;
          return (
            <div className="flex items-center justify-end gap-1.5">
              {h.status === 'open' && (
                <Button
                  size="sm"
                  disabled={!mayWork}
                  title={gate}
                  onClick={(e) => {
                    e.stopPropagation();
                    claim.mutate(h.id);
                  }}
                >
                  Claim
                </Button>
              )}
              {(h.status === 'claimed' || h.status === 'in_progress') && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!mayWork}
                  title={gate ?? 'Hand the thread back to the agent. The handoff stays open.'}
                  onClick={(e) => {
                    e.stopPropagation();
                    release.mutate(h.id);
                  }}
                >
                  Release
                </Button>
              )}
              {h.status !== 'closed' && (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!mayWork}
                  title={gate}
                  onClick={(e) => {
                    e.stopPropagation();
                    setClosing(h);
                  }}
                >
                  Close
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/inbox/${h.conversationId}`);
                }}
              >
                Thread
              </Button>
            </div>
          );
        },
      }),
    ],
    [claim, release, navigate, mayWork, gate],
  );

  if (handoffs.isError) {
    return <ErrorState error={handoffs.error} context="load the handoffs queue" onRetry={() => void handoffs.refetch()} />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-line bg-surface px-4 py-2.5">
        {(['all', 'open', 'claimed', 'closed'] as const).map((s) => (
          <button
            key={s}
            onClick={() => {
              const next = new URLSearchParams(params);
              if (s === 'all') next.delete('status');
              else next.set('status', s);
              setParams(next, { replace: true });
            }}
            className={clsx(
              'rounded-full px-3 py-1 text-xs font-medium capitalize',
              status === s ? 'bg-brand-soft text-brand-soft-fg' : 'text-fg-muted hover:bg-surface-2',
            )}
          >
            {s}
          </button>
        ))}
        {/*
          `GET /handoffs` has no cursor either: `readHandoffs` takes a `limit` (200 by default) and
          answers a bare array. A queue that stops at two hundred without saying so is how a
          handover nobody has claimed becomes invisible, which is the exact failure this screen
          exists to prevent — so it says so. Paging it is a server change; reported, not guessed.
        */}
        {(handoffs.data?.length ?? 0) >= HANDOFF_CAP && (
          <p className="text-[11px] font-medium text-warn">
            Showing the first {HANDOFF_CAP} — the server caps this list and there may be more
          </p>
        )}
        <p className="ml-auto text-[11px] text-fg-subtle">
          {mayWork
            ? `Signed in as ${user.name} · a claim is recorded against your name`
            : `Signed in as ${user.name} (${user.role}) · read-only here — claiming and closing need rep or above`}
        </p>
      </div>

      <div className="min-h-0 flex-1">
        <DataTable
          data={handoffs.data ?? []}
          loading={handoffs.isLoading}
          columns={columns}
          rowKey={(h) => h.id}
          ariaLabel="Handoffs queue"
          selectedKey={detail?.id ?? null}
          onRowActivate={(h) => setDetail(h)}
          emptyTitle="No handoffs in this view"
          emptyBody="When a customer asks for a person, or a policy forces it, it lands here."
        />
      </div>

      <Drawer
        open={Boolean(detail)}
        onOpenChange={(open) => !open && setDetail(null)}
        title={detail ? `${detail.displayName} · ${detail.code}` : ''}
        description={detail?.reasonLabel}
        size="lg"
        footer={
          detail && detail.status !== 'closed' ? (
            <div className="flex items-center justify-end gap-2">
              {gate && <span className="mr-auto text-[11px] text-fg-subtle">{gate}</span>}
              {detail.status === 'open' && (
                <Button disabled={!mayWork} title={gate} onClick={() => claim.mutate(detail.id)}>
                  Claim
                </Button>
              )}
              <Button variant="secondary" disabled={!mayWork} title={gate} onClick={() => setClosing(detail)}>
                Close with an outcome
              </Button>
            </div>
          ) : undefined
        }
      >
        {detail && <HandoffDetail handoff={detail} />}
      </Drawer>

      <Drawer
        open={Boolean(closing)}
        onOpenChange={(open) => !open && setClosing(null)}
        title="Close this handoff"
        description="A handoff cannot be closed without an outcome — the database refuses it, and so does this form."
      >
        {closing && <CloseForm handoff={closing} pending={close.isPending} onClose={(outcome, reason) => close.mutate({ id: closing.id, outcome, reason })} />}
      </Drawer>
    </div>
  );
}

function HandoffDetail({ handoff }: { handoff: Handoff }): ReactNode {
  return (
    <div className="flex flex-col gap-5 text-sm">
      <section>
        <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">Basis</h3>
        <div className="flex items-center gap-2">
          <Pill tone={BASIS_TONE[handoff.basis]}>{handoff.basis.replace('_', ' ')}</Pill>
          <span className="text-xs text-fg-muted">
            {handoff.basis === 'customer_consent'
              ? 'Their own affirmative words, after we asked.'
              : handoff.basis === 'policy_required'
                ? "The business's own rules forced it."
                : 'We decided. Nobody said yes and no rule compelled it.'}
          </span>
        </div>
        {handoff.quote && (
          <blockquote className="mt-2 border-l-2 border-brand bg-surface-2 py-2 pl-3 text-sm italic text-fg">
            “{handoff.quote}”
          </blockquote>
        )}
      </section>

      <section>
        <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">Brief</h3>
        <dl className="flex flex-col gap-1">
          {handoff.brief.map((b) => (
            <div key={b.label} className="grid grid-cols-[10rem_1fr] gap-2 border-b border-line py-1.5 last:border-0">
              <dt className="text-xs text-fg-subtle">{b.label}</dt>
              <dd className={clsx('text-xs', b.value ? 'text-fg' : 'italic text-fg-subtle')}>
                {b.value ?? 'not known'}
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-1.5 text-[11px] text-fg-subtle">
          The nulls are the point — a brief that hides what it does not know is worse than one that says so.
        </p>
      </section>

      {handoff.notice !== null && (
        <section>
          <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
            What the team was sent
          </h3>
          {/*
            A rep notification is deliberately never written into the customer's transcript, so
            without this the message existed only on somebody's handset. `whitespace-pre-wrap`
            because the brief IS its line breaks — flattened onto one line it is unreadable.
          */}
          <pre className="max-h-64 overflow-auto rounded-md bg-surface-2 px-2.5 py-2 text-xs leading-relaxed whitespace-pre-wrap text-fg-muted font-sans">
            {handoff.notice}
          </pre>
        </section>
      )}

      <section>
        <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
          Team notified
        </h3>
        {handoff.notifications.length === 0 ? (
          <p className="text-xs text-fg-muted">
            No delivery attempt was recorded for this handover.
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5 text-xs">
            {handoff.notifications.map((n) => (
              <li
                key={`${n.adapter}-${n.target ?? ''}-${n.step}`}
                className={
                  n.status === 'sent' && n.delivery !== 'failed'
                    ? 'text-fg-muted'
                    : 'rounded-md bg-danger-soft px-2 py-1.5 text-danger-soft-fg'
                }
              >
                <span className="font-medium text-fg">{ADAPTER_LABEL[n.adapter]}</span>
                {/* Null target means the signed-in role may not see phone numbers. */}
                {n.target !== null && n.adapter !== 'inbox' && (
                  <span className="font-mono text-[11px]"> {n.target}</span>
                )}
                {' — '}
                {n.delivery === 'failed' ? 'not delivered' : NOTIFY_REASON[n.status]} ({formatAgo(n.at)})
                {n.step > 0 && <span className="text-fg-subtle"> · chase {n.step}</span>}
                {n.error && <span className="block text-[11px] opacity-80">{n.error}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">Trail</h3>
        <ul className="flex flex-col gap-1.5 text-xs">
          <li className="text-fg-muted">Opened {formatAgo(handoff.openedAt)}</li>
          {handoff.claimedAt && (
            <li className="text-fg-muted">
              Claimed by {handoff.claimedBy} {formatAgo(handoff.claimedAt)}
            </li>
          )}
          {handoff.escalations.map((e) => (
            <li key={e.at} className="rounded-md bg-danger-soft px-2 py-1.5 text-danger-soft-fg">
              {escalationLabel(e.note)} ({formatAgo(e.at)})
            </li>
          ))}
          {handoff.closedAt && (
            <li className="text-fg-muted">
              Closed {formatAgo(handoff.closedAt)} as <strong className="text-fg">{handoff.outcome}</strong>
              {handoff.outcomeReason ? ` — ${handoff.outcomeReason}` : ''}
            </li>
          )}
        </ul>
      </section>
    </div>
  );
}

function CloseForm({
  handoff,
  pending,
  onClose,
}: {
  handoff: Handoff;
  pending: boolean;
  onClose: (outcome: HandoffOutcome, reason: string) => void;
}): ReactNode {
  const [outcome, setOutcome] = useState<HandoffOutcome | null>(null);
  const [reason, setReason] = useState('');
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-fg-muted">
        {handoff.displayName} · <span className="font-mono">{handoff.code}</span>
      </p>
      <fieldset className="flex flex-col gap-1.5">
        <legend className="mb-1 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">Outcome (required)</legend>
        {OUTCOMES.map((o) => (
          <label
            key={o.key}
            className={clsx(
              'flex cursor-pointer items-center gap-2 rounded-[var(--h1-radius)] border px-3 py-2 text-sm',
              outcome === o.key ? 'border-brand bg-brand-soft text-brand-soft-fg' : 'border-line hover:bg-surface-2',
            )}
          >
            <input
              type="radio"
              name="outcome"
              value={o.key}
              checked={outcome === o.key}
              onChange={() => setOutcome(o.key)}
              className="accent-[var(--h1-brand)]"
            />
            {o.label}
          </label>
        ))}
      </fieldset>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
          What happened (required)
        </span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="One line the next person can read in six months."
          className="rounded-[var(--h1-radius)] border border-line-strong bg-bg px-3 py-2 text-sm text-fg"
        />
      </label>
      {/*
        The reason is required by `closeHandoffIn`, not only by this form — "not_reached" with no
        sentence is the same non-answer as no outcome at all, one field further on. The button is
        disabled for the same rule the server enforces, so the refusal is never a surprise 400.
      */}
      <Button disabled={!outcome || !reason.trim() || pending} onClick={() => outcome && onClose(outcome, reason)}>
        {pending ? 'Closing…' : 'Close handoff'}
      </Button>
      {(!outcome || !reason.trim()) && (
        <p className="text-[11px] text-fg-subtle">
          {!outcome ? 'Pick an outcome' : 'Write one line about what happened'} to enable the button — the API refuses
          a close without both.
        </p>
      )}
    </div>
  );
}
